import { useState, useEffect, useRef } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  User,
  Save,
  Loader2,
  Pencil,
  Building2,
  Mail,
  KeyRound,
  CheckCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { platformApi, PlatformUser } from "@/services/api/platform";

export const Route = createFileRoute("/portal/platform/profile")({
  component: ProfilePage,
});

function ProfilePage() {
  const { token, session, setSession } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [profile, setProfile] = useState<PlatformUser | null>(null);
  const [formData, setFormData] = useState({ first_name: "", last_name: "" });

  // Read the token via a ref so an auth-token rotation does NOT re-run this
  // effect. Without it, every keep-alive / 401-driven refresh flips `loading`
  // true (unmounting the form via the gate below) AND calls setFormData(...),
  // wiping the user's unsaved name edit. Profile is the current user's, loaded
  // once on mount (behind the auth gate so the token is always present).
  const tokenRef = useRef(token);
  tokenRef.current = token;

  useEffect(() => {
    let cancelled = false;

    async function loadProfile() {
      const currentToken = tokenRef.current;
      if (!currentToken) return;
      setLoading(true);
      try {
        const data = await platformApi.getMyProfile(currentToken);
        if (cancelled) return;
        setProfile(data);
        setFormData({
          first_name: data.first_name || "",
          last_name: data.last_name || "",
        });
      } catch {
        if (!cancelled) toast.error("Failed to load profile");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadProfile();
    return () => { cancelled = true; };
    // token read via tokenRef so rotation doesn't reset the form / unmount; load once on mount.

  }, []);

  async function handleSave() {
    if (!token) return;
    if (!formData.first_name.trim()) {
      toast.error("First name is required");
      return;
    }

    setSaving(true);
    try {
      const updated = await platformApi.updateMyProfile(token, {
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
      });
      setProfile(updated);
      setIsEditing(false);
      toast.success("Profile updated successfully");

      // Update the session so the header avatar/name reflects the change
      if (session) {
        setSession({
          ...session,
          user: {
            ...session.user,
            firstName: updated.first_name || undefined,
            lastName: updated.last_name || undefined,
          },
        });
      }
    } catch {
      toast.error("Failed to update profile");
    } finally {
      setSaving(false);
    }
  }

  function handleCancel() {
    if (profile) {
      setFormData({
        first_name: profile.first_name || "",
        last_name: profile.last_name || "",
      });
    }
    setIsEditing(false);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="p-6 text-center text-muted-foreground">
        Could not load profile.
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">My Profile</h1>
          <p className="text-sm text-muted-foreground">
            Manage your personal information
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isEditing ? (
            <>
              <Button variant="outline" onClick={handleCancel} disabled={saving}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                Save Changes
              </Button>
            </>
          ) : (
            <Button onClick={() => setIsEditing(true)}>
              <Pencil className="mr-2 h-4 w-4" />
              Edit Profile
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-6">
        {/* Personal Information */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <User className="h-4 w-4" />
              Personal Information
            </CardTitle>
            <CardDescription className="text-xs">
              Your name and contact details
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">First Name</Label>
                <Input
                  id="first_name"
                  value={formData.first_name}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      first_name: e.target.value,
                    }))
                  }
                  disabled={!isEditing}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">Last Name</Label>
                <Input
                  id="last_name"
                  value={formData.last_name}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      last_name: e.target.value,
                    }))
                  }
                  disabled={!isEditing}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">{profile.email}</span>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Username</Label>
              <span className="block text-sm text-muted-foreground">
                {profile.username}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Change Password */}
        <ChangePasswordCard />

        {/* Organisations */}
        {profile.organisations.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Organisations
              </CardTitle>
              <CardDescription className="text-xs">
                Organisations you belong to
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {profile.organisations.map((org) => (
                  <div
                    key={org.organisation_id}
                    className="flex items-center justify-between rounded-md border p-3"
                  >
                    <span className="text-sm font-medium">
                      {org.organisation_name}
                    </span>
                    <Badge variant="secondary" className="text-xs">
                      {org.role.replace("org_", "")}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

const PASSWORD_RULES = [
  { label: "At least 8 characters", test: (p: string) => p.length >= 8 },
  { label: "Uppercase letter", test: (p: string) => /[A-Z]/.test(p) },
  { label: "Lowercase letter", test: (p: string) => /[a-z]/.test(p) },
  { label: "A number", test: (p: string) => /\d/.test(p) },
  {
    label: "Special character",
    test: (p: string) => /[!@#$%^&*(),.?":{}|<>_\-+=\\[\]/~`';]/.test(p),
  },
];

function ChangePasswordCard() {
  const { token } = useAuth();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const allRulesPassed = PASSWORD_RULES.every((r) => r.test(newPassword));
  const passwordsMatch =
    newPassword === confirmPassword && newPassword.length > 0;
  const canSubmit =
    currentPassword.length > 0 && allRulesPassed && passwordsMatch && !saving;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit || !token) return;

    setSaving(true);
    setError(null);

    try {
      await platformApi.changePassword(token, {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success("Password changed successfully");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err: unknown) {
      const detail = (err as { details?: { detail?: string } })?.details
        ?.detail;
      if (detail === "Current password is incorrect") {
        setError("Current password is incorrect");
      } else {
        toast.error(detail || "Failed to change password");
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <KeyRound className="h-4 w-4" />
          Change Password
        </CardTitle>
        <CardDescription className="text-xs">
          Update your password
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="current-password">Current password</Label>
            <Input
              id="current-password"
              type="password"
              value={currentPassword}
              onChange={(e) => {
                setCurrentPassword(e.target.value);
                setError(null);
              }}
              autoComplete="current-password"
              disabled={saving}
            />
            {error && <p className="text-xs text-destructive">{error}</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="new-password">New password</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                disabled={saving}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirm new password</Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                disabled={saving}
              />
              {confirmPassword && !passwordsMatch && (
                <p className="text-xs text-destructive">
                  Passwords do not match
                </p>
              )}
            </div>
          </div>

          {newPassword && (
            <div className="space-y-1.5 rounded-lg bg-slate-50 p-3">
              {PASSWORD_RULES.map((rule) => {
                const passed = rule.test(newPassword);
                return (
                  <div
                    key={rule.label}
                    className={`flex items-center gap-2 text-xs ${passed ? "text-green-600" : "text-muted-foreground"}`}
                  >
                    {passed ? (
                      <CheckCircle className="h-3 w-3" />
                    ) : (
                      <span className="h-3 w-3 rounded-full border border-current" />
                    )}
                    {rule.label}
                  </div>
                );
              })}
            </div>
          )}

          <div className="flex justify-end">
            <Button type="submit" disabled={!canSubmit}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Change Password
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
